import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import moment from 'moment';
import {
  ClosableModal,
  PrimaryButton,
  PrimaryChip,
  PrimaryChipsSlider,
  PrimaryLiteButton,
  PrimaryTable,
} from 'components';
import { coreSliceActions } from 'base/core/slice';
import { useDispatch, useSelector } from 'react-redux';
import { createSessionActions } from '../../slice';
import { IPendingSlotItem } from '../../interface';
import { selectModalLoader, selectPendingSlots } from '../../selector';

interface CancelSessionSlotsModelProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
}

interface IListItem extends IPendingSlotItem {
  isSelected: boolean;
  rowId: string;
}

const parseSlotMoment = (item: Pick<IPendingSlotItem, 'date' | 'from'>) => {
  const hasDateTime = item.from?.includes('T') || item.from?.includes('-');
  if (hasDateTime) {
    return moment.utc(item.from);
  }
  return moment(`${item.date} ${item.from}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm']);
};

const formatSlotTime = (value?: string, fallbackDate?: string) => {
  if (!value) {
    return '-';
  }

  const hasDateTime = value.includes('T') || value.includes('-');
  if (hasDateTime) {
    return moment(value).format('hh:mm A');
  }

  return moment(`${fallbackDate || ''} ${value}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm']).format('hh:mm A');
};

const CancelSessionSlotsModel = ({
  open,
  onClose,
  sessionId,
}: CancelSessionSlotsModelProps) => {
  const dispatch = useDispatch();
  const pendingSlots = useSelector(selectPendingSlots);
  const loading = useSelector(selectModalLoader);

  const [selectedItems, setSelectedItems] = React.useState<IListItem[]>([]);
  const [filteredList, setFilteredList] = React.useState<IListItem[]>([]);

  React.useEffect(() => {
    if (pendingSlots && Array.isArray(pendingSlots) && pendingSlots.length > 0) {
      const uniqueSlots = pendingSlots.filter((item, index, array) => {
        const key = `${item.from}__${item.to}`;
        return array.findIndex((slot) => `${slot.from}__${slot.to}` === key) === index;
      });

      setFilteredList(
        uniqueSlots
          .slice()
          .sort((left, right) => parseSlotMoment(left).diff(parseSlotMoment(right)))
          .map((item, index) => {
            const rowId = item.meetingId ?? `${item.date}-${item.from}-${item.to}-${index}`;
            return {
              ...item,
              rowId,
              isSelected: selectedItems.some(
                (selectedItem) => selectedItem.rowId === rowId,
              ),
            };
          }),
      );
    } else {
      setFilteredList([]);
    }
  }, [pendingSlots, selectedItems]);

  const handleKeepOldSessions = () => {
    setSelectedItems([]);
    onClose();
  };

  const handleContinue = async () => {
    try {
      const meetingIds = selectedItems
        .map((item) => item.meetingId)
        .filter((meetingId): meetingId is string => Boolean(meetingId));

      const slotsPayload = selectedItems
        .filter((item) => !item.meetingId)
        .map((item) => ({ from: item.from, to: item.to }));

      await new Promise((resolve, reject) => {
        dispatch(createSessionActions.bulkCancelSession({
          id: sessionId,
          ...(meetingIds.length > 0 ? { meetingIds } : {}),
          ...(slotsPayload.length > 0 ? { slots: slotsPayload } : {}),
          resolve,
          reject,
        }));
      });

      dispatch(coreSliceActions.setMessage({
        message: 'Session booking has been cancelled successfully.',
        success: true,
      }));
      onClose();
    } catch (error) {
      dispatch(coreSliceActions.setMessage({
        message: 'Session booking cancellation failed. Please try again.',
        success: false,
      }));
    }
  };

  const handleRemoveItem = (rowId: string) => {
    const temp = [...filteredList];
    const index = temp.findIndex((item) => item.rowId === rowId);
    if (index > -1) {
      temp[index].isSelected = false;
      setFilteredList(temp);
    }

    setSelectedItems(selectedItems.filter((item) => item.rowId !== rowId));
  };

  const addItem = React.useCallback((rowId: string) => {
    const temp = [...filteredList];
    const index = temp.findIndex((item) => item.rowId === rowId);
    if (index > -1) {
      if (temp[index].isSelected) {
        return;
      }
      temp[index].isSelected = true;
      setFilteredList(temp);
      setSelectedItems([...selectedItems, temp[index]]);
    }
  }, [filteredList, selectedItems]);

  type Row = IListItem;

  const itemTableColumns = React.useMemo<GridColumns<Row>>(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        flex: 0.3,
        minWidth: 80,
        renderCell: (params) => moment(params.value).format('DD/MM/YYYY'),
      },
      {
        field: 'from',
        headerName: 'Start At',
        flex: 0.3,
        minWidth: 80,
        renderCell: (params) => formatSlotTime(params.value, params.row.date),
      },
      {
        field: 'to',
        headerName: 'End At',
        flex: 0.3,
        minWidth: 80,
        renderCell: (params) => formatSlotTime(params.value, params.row.date),
      },
      {
        field: 'isSelected',
        headerName: '',
        headerAlign: 'left',
        align: 'left',
        flex: 0.2,
        renderCell: (params) => (
          <div>
            {params.value ? (
              <Typography variant="body2" component="p" color="primary">
                Selected
              </Typography>
            ) : (
              <PrimaryLiteButton
                onClick={() => { addItem(params.row.rowId); }}
                disabled={loading}
              >
                Select
              </PrimaryLiteButton>
            )}
          </div>
        ),
      },
    ],
    [addItem, loading],
  );

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title="Your session has been updated successfully!"
      subTitle="If you need to cancel the current bookings, select and cancel below."
      modalClass="select-item-modal"
    >
      {selectedItems.length > 0 && (
        <Box mb={2}>
          <div>
            <PrimaryChipsSlider>
              {selectedItems.map((item: IListItem) => (
                <PrimaryChip
                  key={item.rowId}
                  label={`${moment(item.date, 'YYYY-MM-DD').format('DD/MM/YYYY')} ${formatSlotTime(item.from, item.date)} - ${formatSlotTime(item.to, item.date)}`}
                  variant="outlined"
                  onDelete={() => { handleRemoveItem(item.rowId); }}
                />
              ))}
            </PrimaryChipsSlider>
          </div>
        </Box>
      )}
      <div>
        <div>
          <PrimaryTable
            disableSelectionOnClick
            rows={filteredList}
            columns={itemTableColumns}
            getRowId={(row) => row.rowId}
            loading={loading}
            hideFooter={false}
            pagination
            pageSize={5}
            rowsPerPageOptions={[5]}
            rowCount={filteredList.length}
          />
        </div>
      </div>
      <Box display="flex" gap={4} mt={4}>
        <Box sx={{ flex: 1 }}>
          <PrimaryLiteButton fullWidth onClick={handleKeepOldSessions}>
            Keep old sessions
          </PrimaryLiteButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          <PrimaryButton
            disabled={!selectedItems || selectedItems.length === 0 || loading}
            fullWidth
            onClick={handleContinue}
            color="error"
          >
            Cancel sessions
          </PrimaryButton>
        </Box>
      </Box>
    </ClosableModal>
  );
};

export default CancelSessionSlotsModel;
